import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TLD, expandTld } from 'f2tech-shared';
import { ApiService } from '../@services/api.service';
import { AuthService } from '../@services/auth.service';
import { environment as env } from '../../environments/environment';

/**
 * Allowed-scanner-host policy is sourced ENTIRELY from
 * F2-ADMIN.Customers (`domains[]`) ∪ F2-ADMIN.Scanners (`Url`, `AppUrl`)
 * via the backend `/rest/api/customer-domains` endpoint. Adding a
 * customer or scanner in the admin UI is the only way to widen the set;
 * there are no hardcoded fallback lists in source.
 *
 * Every host returned by the endpoint is implicitly trusted to:
 *   - serve a sid-capable bundle (POST /rest/auth/redeem-session)
 *   - call `.f2-tech.ai` backends cross-origin
 * If a destination isn't yet sid-migrated, don't add its host to the
 * Scanner/Customer record until it is.
 *
 * Same-TLD enforcement: admin on `.${TLD}` may open scanners on
 * `*.f2-tech.${TLD}` OR any host in the dynamic allowlist. Cross-TLD
 * opens against unknown hosts are blocked.
 */

@Component({
  selector: 'app-home',
  imports: [CommonModule, FormsModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {

  scanners: any[] = [];

  // Tracks which Url just got copied so the cell shows "Copied!" briefly
  // (one row at a time, cleared after 1.2s). Indexed by scanner.Id.
  copiedScannerId: string | null = null;

  copyScannerUrl(scanner: any, ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    const url = (scanner?.Url || scanner?.AppUrl || '').trim();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      this.copiedScannerId = scanner.Id;
      setTimeout(() => {
        if (this.copiedScannerId === scanner.Id) this.copiedScannerId = null;
      }, 1200);
    });
  }

  // Dynamic customer-host allowlist fetched from F2-ADMIN.Customers.
  // Replaces the hardcoded CUSTOMER_HOSTS Set for the same-TLD bypass —
  // adding a customer host is now a Mongo upsert, not a SPA redeploy.
  // The hardcoded list above is still consulted as a transition-safety
  // fallback (legacy hosts still work until each gets a Customers row).
  dynamicCustomerHosts: Set<string> = new Set();

  constructor(private apiService: ApiService,
    public authService: AuthService,
    private http: HttpClient
  ) {

  }

  ngOnInit(): void {
    this.get_user_scanners();
    this.fetch_customer_domains();
  }

  async fetch_customer_domains() {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/api/customer-domains`, { withCredentials: true })
        .toPromise();
      const arr: string[] = Array.isArray(res?.domains) ? res.domains : [];
      this.dynamicCustomerHosts = new Set(arr.map(h => String(h).toLowerCase()));
    } catch (e) {
      // Endpoint failure leaves the set empty — same-TLD check still
      // honors `.f2-tech.${TLD}` so .ai scanners keep working.
      console.warn('customer-domains fetch failed:', e);
    }
  }

  async get_user_scanners() {
    this.scanners = [];
    let res = await this.apiService.getUserScanners();
    if (res && !res.err) {
      this.scanners = res;
    }
    console.log('User Scanners:', this.scanners);
  }

  /**
   * Open a scanner via the sid handoff path. Mirrors the two-outcomes rule
   * from f2-members:
   *
   *   1. SID-capable host (allowlist above) → POST mint-scanner-sid-cookie
   *      (the cookie-authed variant; admin SPA has no JWT in JS) → navigate
   *      to the returned URL with `?sid=<43char>`.
   *   2. NOT sid-capable → BLOCK with an alert. NO navigation, NO token
   *      in URL, NO `.com` fallback, NO bare-URL emit. The tile is dead
   *      until that SPA migrates to a sid reader (mirrors MTA/TheoTrade
   *      B2) and is added to SID_CAPABLE_HOSTS above.
   *
   * Same-TLD enforcement (HARD RULE): admin on `.ai` may ONLY open
   * scanners on `*.f2-tech.ai`. Cross-TLD opens are blocked with the
   * same alert pattern.
   */
  async navigate_to_scanner(scanner: any, event: any) {
    if (!scanner._id) return;
    // Fall back to AppUrl when Url is blank (e.g. scanners that only
    // know their SPA root, not a per-scanner deep link). Either field
    // is acceptable for the tile click — the customer's SPA router
    // figures out the rest.
    const effectiveUrl = scanner.Url || scanner.AppUrl;
    if (!effectiveUrl) return;

    // Expand `{tld}` placeholder + resolve host. Legacy rows that hardcode
    // `.f2-tech.com` pass through unchanged (and get blocked by same-TLD).
    const expanded = expandTld(effectiveUrl);
    let target: URL;
    try { target = new URL(expanded); } catch { return; }
    const hostname = target.hostname.toLowerCase();

    // Same-TLD enforcement. Cross-TLD opens propagate reputation signals
    // between domains and a .ai admin's browser may not even reach .com.
    // Customer-owned hosts (CUSTOMER_HOSTS above) are exempt — they ship
    // our bundle on a customer domain, verified at registration time.
    const expectedSuffix = `.f2-tech.${TLD}`;
    if (!hostname.endsWith(expectedSuffix) && !this.dynamicCustomerHosts.has(hostname)) {
      const msg = `Scanner "${scanner.Name}" is not yet available on .${TLD}. (${hostname})`;
      console.warn('Cross-TLD click blocked:', { tld: TLD, scanner, expanded });
      alert(msg);
      return;
    }

    // Sid-capable path: backend reads admin's id_token from the f2_id
    // cookie (admin has no JWT in JS by design — cookie/PKCE flow), mints
    // a sid carrying a partial bundle, returns `<scanner-host>/scans/<scan>?sid=<43char>`.
    // JWT never in URL; admin's cookie never leaves the same-TLD origin.
    let body: any;
    try {
      body = await this.http
        .post(`${env.apiUrl}/rest/auth/mint-scanner-sid-cookie`,
          { scannerId: scanner._id },
          { withCredentials: true })
        .toPromise();
    } catch (e: any) {
      const status = e?.status;
      if (status === 401) {
        // Cookie missing/expired — bounce through the admin's normal auth
        // path. The shared auth service knows how to recover.
        console.warn('mint-scanner-sid-cookie 401 — session expired');
        alert(`Session expired — please sign in again.`);
        return;
      }
      if (status === 403) {
        alert(`You're not entitled to "${scanner.Name}".`);
        return;
      }
      if (status === 400) {
        alert(`Scanner "${scanner.Name}" is not yet available on .${TLD}.`);
        return;
      }
      console.warn('mint-scanner-sid-cookie failed:', e);
      alert(`Failed to open "${scanner.Name}" (${status ?? 'network'}).`);
      return;
    }

    const url = (body as any)?.url as string | undefined;
    if (!url) return;

    // Defense-in-depth on the URL the backend returned:
    //   (a) must still be same-TLD
    //   (b) must STILL be sid-capable (matches our allowlist)
    // If either fails, BLOCK — never navigate to an unsanctioned host.
    let returnedUrl: URL;
    try { returnedUrl = new URL(url); } catch { return; }
    const returnedHost = returnedUrl.hostname.toLowerCase();
    if (!returnedHost.endsWith(expectedSuffix) && !this.dynamicCustomerHosts.has(returnedHost)) {
      console.warn('Cross-TLD url returned from mint endpoint, blocking:', url);
      alert(`Scanner "${scanner.Name}" is not yet available on .${TLD}. (${returnedHost})`);
      return;
    }

    if (event.altKey === true) {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
  }
}
